import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "./DepositScreen.css";
import { useTranslation } from "../localization";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api";

function TransactionsScreen({
  walletAddress,
  authToken,
  inline = false,
  refreshTrigger = 0,
}) {
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState([]);
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const [depositInfo, setDepositInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchTransactions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/wallet/transactions`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      const newTransactions = response.data.transactions || [];
      setTransactions(newTransactions);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  const fetchPendingDeposits = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/deposits/pending`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      setPendingDeposits(response.data.pending || []);
      setDepositInfo(response.data.depositInfo || null);
    } catch (error) {
      console.error("Error fetching pending deposits:", error);
      setPendingDeposits([]);
    }
  }, [authToken]);

  useEffect(() => {
    if (walletAddress) {
      fetchTransactions();
      fetchPendingDeposits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletAddress, refreshTrigger]);

  // Poll for deposit progress every 10 seconds
  useEffect(() => {
    if (!walletAddress || pendingDeposits.length === 0) return;

    const interval = setInterval(() => {
      fetchPendingDeposits();
    }, 10000);

    return () => clearInterval(interval);
  }, [walletAddress, pendingDeposits.length, fetchPendingDeposits]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().substring(11, 19) + " UTC";
  };

  const getTransactionType = (tx) => {
    const type = tx.type || tx.transaction_type;
    if (type === "deposit") return t("transactions.deposit");
    if (type === "withdrawal") return t("transactions.withdrawal");
    if (type === "bid") return t("transactions.bid");
    if (type === "win" || type === "prize") return t("transactions.win");
    return type;
  };

  // Group transactions by game
  const groupTransactionsByGame = (transactions) => {
    const grouped = {};
    const nonGameTransactions = [];

    transactions.forEach((tx) => {
      if (tx.game_id && (tx.type === "bid" || tx.type === "win" || tx.type === "prize")) {
        if (!grouped[tx.game_id]) {
          grouped[tx.game_id] = [];
        }
        grouped[tx.game_id].push(tx);
      } else {
        nonGameTransactions.push(tx);
      }
    });

    // Calculate net result for each game
    const gamesWithSummary = Object.entries(grouped).map(([gameId, txs]) => {
      const netAmount = txs.reduce((sum, tx) => {
        const amount = parseFloat(tx.amount);
        if (tx.type === "bid") return sum - amount;
        if (tx.type === "win" || tx.type === "prize") return sum + amount;
        return sum;
      }, 0);

      return {
        gameId,
        transactions: txs.sort((a, b) => {
          // Sort bids before wins
          if (a.type === "bid" && (b.type === "win" || b.type === "prize")) return -1;
          if ((a.type === "win" || a.type === "prize") && b.type === "bid") return 1;
          // Otherwise sort by date
          return new Date(b.created_at) - new Date(a.created_at);
        }),
        netAmount,
      };
    });

    // Sort games by most recent transaction
    gamesWithSummary.sort((a, b) => {
      const aLatest = new Date(a.transactions[0].created_at);
      const bLatest = new Date(b.transactions[0].created_at);
      return bLatest - aLatest;
    });

    return { games: gamesWithSummary, nonGameTransactions };
  };

  if (!walletAddress) {
    return (
      <div className={inline ? "transactions-inline" : "transactions-screen"}>
        {!inline && (
          <h2 className="screen-title">{t("transactions.history")}</h2>
        )}
        <div className="no-transactions">
          <p>{t("transactions.connect")}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={inline ? "transactions-inline" : "transactions-screen"}>
        {!inline && (
          <h2 className="screen-title">{t("transactions.history")}</h2>
        )}
        <div className="no-transactions">
          <p>{t("transactions.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={inline ? "transactions-inline" : "transactions-screen"}>
      {!inline && (
        <h2 className="screen-title">{t("transactions.history")}</h2>
      )}

      {/* Pending Deposits */}
      {pendingDeposits.length > 0 && (
        <div className="pending-deposits-section">
          <h3 className="section-title">
            {t("transactions.pendingDeposits")}
          </h3>
          {pendingDeposits.map((deposit) => (
            <div key={deposit.id} className="pending-deposit-item">
              <div className="deposit-header">
                <div className="deposit-amount">
                  +${parseFloat(deposit.amount).toFixed(2)}
                </div>
                <div className="deposit-status">
                  {deposit.confirmations}/
                  {depositInfo?.confirmationsRequired || 64}{" "}
                  {t("transactions.confirmations")}
                </div>
              </div>
              <div className="progress-bar-container">
                <div
                  className="progress-bar-fill"
                  style={{
                    width: `${Math.min(
                      (deposit.confirmations /
                        (depositInfo?.confirmationsRequired || 64)) *
                        100,
                      100
                    )}%`,
                  }}
                />
              </div>
              {deposit.txHash && (
                <div className="deposit-tx-hash">
                  <a
                    href={`https://polygonscan.com/tx/${deposit.txHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {deposit.txHash.substring(0, 10)}...
                    {deposit.txHash.substring(deposit.txHash.length - 8)} 🔍
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {transactions.length === 0 ? (
        <div className="no-transactions">
          <p>{t("transactions.empty")}</p>
        </div>
      ) : (
        <div className="transaction-list">
          {(() => {
            const { games, nonGameTransactions } = groupTransactionsByGame(transactions);

            return (
              <>
                {/* Non-game transactions (deposits, withdrawals) */}
                {nonGameTransactions.map((tx, index) => (
                  <div key={tx.id || `non-game-${index}`} className={`transaction-item ${tx.type || tx.transaction_type}`}>
                    <div className="transaction-header">
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px", flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                          <span className={`transaction-type ${tx.type || tx.transaction_type}`}>
                            {getTransactionType(tx)}
                          </span>
                          <span className="transaction-date">
                            {formatDate(tx.created_at)}
                          </span>
                        </div>
                        <div className="transaction-details">
                          {tx.wallet_address && (
                            <span className="wallet-info">
                              {tx.wallet_address.substring(0, 6)}...
                              {tx.wallet_address.substring(tx.wallet_address.length - 4)}
                            </span>
                          )}
                          {tx.tx_hash && (
                            <a
                              href={`https://polygonscan.com/tx/${tx.tx_hash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="tx-link"
                              style={{ fontSize: "11px" }}
                            >
                              🔍 {t("transactions.explorer")}
                            </a>
                          )}
                        </div>
                      </div>
                      <span className="transaction-amount">
                        {tx.type === "withdrawal" ? "-" : "+"}
                        ${parseFloat(tx.amount).toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}

                {/* Game-grouped transactions */}
                {games.map((game) => {
                  const gameTime = game.transactions[0]?.created_at;
                  const gameIdShort = game.gameId.substring(0, 8);
                  return (
                    <div key={game.gameId} className="game-group">
                      <div className="game-group-header">
                        <span className="game-group-time">{formatDate(gameTime)} • {gameIdShort}</span>
                      </div>
                      <div className="game-group-transactions">
                        {game.transactions.map((tx, index) => (
                          <div key={tx.id || `game-${game.gameId}-${index}`} className={`transaction-item ${tx.type || tx.transaction_type}`}>
                            <span className={`transaction-type ${tx.type || tx.transaction_type}`}>
                              {getTransactionType(tx)}
                            </span>
                            <span className="transaction-amount">
                              {tx.type === "bid" ? "-" : "+"}
                              ${parseFloat(tx.amount).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

export default TransactionsScreen;
